<template>
  <ElInput
    :id="id"
    v-model="model"
    type="tel"
    :placeholder="placeholder"
  />
</template>

<script setup lang="ts">
import { ElInput } from 'element-plus';
import parsePhoneNumber from 'libphonenumber-js/max';
import { computed, watch } from 'vue';

interface Props {
  modelValue: string | null;
  /** Requires a mobile number; without this prop a landline is expected. */
  mobile?: boolean;
  countryCode?: 'DE';
  placeholder?: string;
  id?: string;
}

const props = withDefaults(defineProps<Props>(), {
  countryCode: 'DE',
  placeholder: '+49 123 4567890',
  id: undefined,
});

const emit = defineEmits<{
  'update:modelValue': [value: string];
  valid: [];
  invalid: [];
}>();

// Strips everything but digits, spaces and a leading plus — otherwise libphonenumber
// trips over parentheses and dashes pasted in from elsewhere.
function clean(value: string | null): string {
  return (value ?? '').replace(/[^\d\s+]/g, '').trimStart();
}

// Only valid if it also matches the required line type: `mobile` wants a mobile
// number, otherwise a landline.
function parse(value: string) {
  const number = parsePhoneNumber(value, props.countryCode);

  if (!number?.isValid()) return null;

  return number.getType() === (props.mobile ? 'MOBILE' : 'FIXED_LINE') ? number : null;
}

const model = computed({
  get: () => {
    const cleaned = clean(props.modelValue);

    return parse(cleaned)?.formatInternational() ?? cleaned;
  },
  set: (value: string) => {
    const cleaned = clean(value);

    emit('update:modelValue', parse(cleaned)?.formatInternational() ?? cleaned);
  },
});

watch(() => props.modelValue, (value) => {
  if (parse(clean(value)) === null) {
    emit('invalid');
  } else {
    emit('valid');
  }
}, { immediate: true });
</script>
